import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { DownloadLink } from '../publications/styled';
import {
    BottomSection,
    ChooseLangText,
    Container,
    ControlsWrapper,
    Description,
    GrayBox,
    LangButton,
    LangSwitcher,
    Title,
} from './styled';
import { HandbookRegisterModal } from 'components';
import { ModalElement } from 'kl-b2c-ui-kit';
import { HandbookRegistration, HandbookLanguage } from 'types';
import { useEvent } from 'contexts';
import DOMPurify from 'dompurify';

export const SUPPORTED_LANGS = [
    { id: 'en', label: 'ENGLISH', enumValue: HandbookLanguage.English },
    {
        id: 'id',
        label: 'BAHASA INDONESIA',
        enumValue: HandbookLanguage.Indonesian,
    },
    {
        id: 'vi',
        label: 'VIETNAMESE',
        enumValue: HandbookLanguage.Vietnamese,
    },
];

export const VALID_LANG_IDS = SUPPORTED_LANGS.map((lang) => lang.id);

export const Handbook = () => {
    const { t } = useTranslation('pages/kids-cyber-resilience');
    const navigate = useNavigate();
    const { langId } = useParams<{ langId: string }>();
    const { registerHandbook } = useEvent();

    const [activeLang, setActiveLang] = useState<string>('en');

    useEffect(() => {
        const isValidLang = langId ? VALID_LANG_IDS.includes(langId) : false;

        if (langId && isValidLang) {
            setActiveLang(langId);
        } else if (langId && !isValidLang) {
            navigate('/kids-cyber-resilience', { replace: true });
        }
    }, [langId, navigate]);

    const handleLangClick = (id: string) => {
        navigate(`/kids-cyber-resilience/${id}`);
    };

    const modalRef = useRef<ModalElement>(null);

    const onRegister = (data: HandbookRegistration) => registerHandbook(data);

    return (
        <>
            <Container>
                <Title
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(t(`handbook.en.title`)),
                    }}
                />
                <GrayBox>
                    <Description>{t(`handbook.en.description`)}</Description>
                    <BottomSection>
                        <ControlsWrapper>
                            <ChooseLangText>
                                {t(`handbook.en.chooseLanguage`)}
                            </ChooseLangText>
                            <LangSwitcher>
                                {SUPPORTED_LANGS.map((lang) => (
                                    <LangButton
                                        key={lang.id}
                                        $isActive={activeLang === lang.id}
                                        onClick={() =>
                                            handleLangClick(lang.id)
                                        }>
                                        {lang.label}
                                    </LangButton>
                                ))}
                            </LangSwitcher>
                        </ControlsWrapper>
                        <DownloadLink
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                modalRef.current?.open();
                            }}>
                            {t(`handbook.en.downloadAll`)}
                        </DownloadLink>
                    </BottomSection>
                </GrayBox>
            </Container>

            <HandbookRegisterModal
                register={onRegister}
                ref={modalRef}
                activeLang={activeLang}
            />
        </>
    );
};
